import React, { useState, useContext, createContext, ReactNode } from 'react';

import { UserJson } from '../types/models';
// Use the new API client
import { apiClient } from '../api';
import { tokenStorage } from '../utils/token-storage';
import { faroLogger } from '../observability/faro-logger';
import { faroInstance } from '../observability/faro';

interface AuthContextInterface {
  user: UserJson | null | boolean;
  signin: (username: string, password: string) => void;
  signout: () => Promise<void>;
  autoSignIn: () => void;
  errors: string[];
  isLoading: boolean;
}

const authContext = createContext<AuthContextInterface | null>(null);
// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().

const AuthProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};
export default AuthProvider;

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = (): AuthContextInterface => {
  // Changer le type de retour ici
  const context = useContext(authContext);
  if (context === null) {
    // Cette erreur ne devrait jamais se produire si <ProvideAuth> est bien placé
    throw new Error('useAuth must be used within a ProvideAuth component');
  }
  return context; // Garanti non-null ici
};
// Provider hook that creates auth object and handles state
function useProvideAuth(): AuthContextInterface {
  const [user, setUser] = useState<UserJson | null | boolean>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Wrap any Firebase methods we want to use making sure ...
  // ... to save the user to state.
  const signin = (username: string, password: string): void => {
    setIsLoading(true);

    apiClient.auth
      .login({
        username,
        password,
      })
      .then((response) => {
        setIsLoading(false);
        console.log(response);
        if (response.user?.email) {
          // Store JWT token
          if (response.jwt?.access_token) {
            tokenStorage.setToken(response.jwt.access_token);
          }
          setUser(response.user);

          // Identify user in Faro
          faroInstance?.api.setUser({
            id: response.user.id?.toString(),
            email: response.user.email,
            username: response.user.lastname || username,
          });

          faroLogger.info('login_success', {
            userId: response.user.id,
            userEmail: response.user.email,
            username: username,
          });
        } else {
          setErrors(['Identifiants incorrects!']);
          faroLogger.warn('login_failed', {
            username: username,
            reason: 'invalid_credentials',
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setErrors(['Erreur de service !']);
        faroLogger.error('login_error', {
          username: username,
          error: error?.message || 'Service error',
        });
      });
  };

  const signout = (): Promise<void> => {
    const currentUser = user && typeof user !== 'boolean' ? user : null;
    tokenStorage.removeToken();
    setUser(false);
    setErrors([]);

    // Reset user in Faro
    faroInstance?.api.resetUser();

    if (currentUser) {
      faroLogger.info('logout', {
        userId: currentUser.id,
        userEmail: currentUser.email,
      });
    }

    return Promise.resolve();
  };

  const autoSignIn = (): Promise<void> => {
    // Skip profile check if no token exists
    if (!tokenStorage.hasToken()) {
      setUser(false);
      return Promise.resolve();
    }

    return apiClient.auth
      .getProfile()
      .then((response) => {
        if (response.user?.email) {
          setUser(response.user);

          // Identify user in Faro on auto-login
          faroInstance?.api.setUser({
            id: response.user.id?.toString(),
            email: response.user.email,
            username: response.user.lastname,
          });

          faroLogger.info('auto_login_success', {
            userId: response.user.id,
            userEmail: response.user.email,
          });
        } else {
          // Clear token if profile check fails
          tokenStorage.removeToken();
          setUser(false);
          faroLogger.warn('auto_login_failed', {
            reason: 'invalid_profile',
          });
        }
      })
      .catch((error) => {
        // Clear token on error
        tokenStorage.removeToken();
        setUser(false);
        faroLogger.warn('auto_login_error', {
          error: error?.message || 'Unknown error',
        });
      });
  };

  // Subscribe to user on mount
  // Because this sets state in the callback it will cause any ...
  // ... component that utilizes this hook to re-render with the ...
  // ... latest auth object.
  // useEffect(() => {
  //   const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
  //     if (user) {
  //       setUser(user);
  //     } else {
  //       setUser(null);
  //     }
  //   });
  //   // Cleanup subscription on unmount
  //   return () => unsubscribe();
  // }, []);
  // Return the user object and auth methods
  return {
    user,
    signin,
    signout,
    autoSignIn,
    errors,
    isLoading,
  };
}
